import store from '../../GameKitUI/store';
import BlokEditor from './BlokEditor';
import Vector from '../../Game/Base/Math/Vector';
import LevelOverlay from './LevelOverlay';
import { ViewPortScaling } from '../../Game';
import TileEditor from './TileEditor';
import { playerClose, playerOpen } from '../../GameKitUI/actions/gameKitActions';
import { setAllLevelSettings } from '../../GameKitUI/actions/levelSettingsActions';
import { updateLevelDescription } from '../../GameKitUI/actions/gameActions';

export default class LevelEditor {
    constructor(gameKit) {
        this.gameKit = gameKit;
        this.level = null;

        this.levelOverlay = new LevelOverlay(this);
        this.blokEditor = new BlokEditor(this);
        this.tileEditor = new TileEditor(this);

        this.dragOffset = new Vector();

        this.inMoveMode = false;
        this.moveModeScale = new Vector(0, 1);
        this.moveModeGoal = this.moveModeScale.copy();

        this.inTileMode = false;
    }

    update(deltaTime) {
        // this.moveModeScale.moveTo(this.moveModeGoal, deltaTime * 0.01);
        // this.gameKit.game.setScale(this.moveModeScale.y, this.moveModeScale.y);

        if (this.level) {
            const { game } = this.gameKit;
            if (game.downKeys[' ']) {
                this.onMouseDown(game.mousePosition);
            } else if (game.upKeys[' ']) {
                this.onMouseDown(game.mousePosition);
            } else if (game.keys[' ']) {
                this.onMouseMove(game.mousePosition);
            }
        }
    }

    onMouseDown(mousePositionP) {
        const mousePosition = this.gameKit.game.canvasToGamePosition(mousePositionP, false);
        this.dragOffset = new Vector();
        this.dragOffset.x = this.level.view.x - -mousePosition.x;
        this.dragOffset.y = this.level.view.y - -mousePosition.y;
    }

    onMouseMove(mousePositionP) {
        const mousePosition = this.gameKit.game.canvasToGamePosition(mousePositionP, false);
        this.level.view.x = -mousePosition.x + this.dragOffset.x;
        this.level.view.y = -mousePosition.y + this.dragOffset.y;
    }

    onMouseUp(mousePositionP) {}

    attachToLevel(level) {
        this.levelOverlay.attachToStepParent(level);
        if (this.inTileMode) {
            this.tileEditor.attachToStepParent(level);
            level.background.blur = false;
        } else {
            this.blokEditor.attachToLevel(level);
            level.background.blur = true;
        }
        level.background.requestNewVisuals();
        this.level = level;
    }

    detachFromLevel() {
        if (!this.level) return;
        if (this.inTileMode) {
            this.tileEditor.detachFromStepParent();
        } else {
            this.blokEditor.detachFromLevel();
        }
        this.level.background.blur = true;
        this.level.background.requestNewVisuals();
        this.levelOverlay.detachFromStepParent();
        this.level = null;
    }

    getLevelSettings() {
        return {
            title: this.level.title,
            description: this.level.description,
            backgroundColorA: this.level.backgroundColorA,
            backgroundColorB: this.level.backgroundColorB,
            view: this.level.viewDirection,
            timeLimit: this.level.timeLimit,
            pointsGoal: this.level.pointsGoal
        };
    }

    // = // UI COMMUNICATION FUNCTIONS // = //

    // Update level settings Optional dispatch parameter (default: true) to dispatch to store.
    // Settings parameter: {
    //  title: string
    //  description: string
    //  backgroundColorA: number (0xffffff)
    //  backgroundColorB: number (0xffffff)
    //  view: 0 platformer, 1 topdown
    //  timeLimit: number : Seconds
    //  pointsGoal: number
    // }
    updateLevelSettings(settings, dispatch = true) {
        if ('title' in settings) {
            this.level.title = settings.title;
        }
        if ('description' in settings) {
            this.level.description = settings.description;
        }
        if ('timeLimit' in settings) {
            this.level.timeLimit = settings.timeLimit;
        }
        if ('pointsGoal' in settings) {
            this.level.pointsGoal = settings.pointsGoal;
        }
        if ('backgroundColorA' in settings) {
            this.level.backgroundColorA = settings.backgroundColorA;
            this.level.updateBackgroundVisuals();
        }
        if ('backgroundColorB' in settings) {
            this.level.backgroundColorB = settings.backgroundColorB;
            this.level.updateBackgroundVisuals();
        }
        if ('view' in settings) {
            this.level.viewDirection = settings.view;
            this.level.player.requestNewVisuals();
            this.level.updateBackgroundVisuals();
            this.level.background.requestNewVisuals();
        }
        if (dispatch) {
            // Update de store
            store.dispatch(setAllLevelSettings(this.getLevelSettings()));
            store.dispatch(updateLevelDescription(this.level.uuid, this.level.description));
        }
    }

    // Start testing the level.
    startTest(dispatch = true) {
        if (dispatch) {
            store.dispatch(playerOpen());
        }
        this.gameKit.gamePlayer.resetPlaySessionValues();
        this.gameKit.gamePlayer.moveRenderer();
        this.gameKit.game.setViewPortScaling(ViewPortScaling.GamePlayBest);
        const { uuid } = this.level;
        this.detachFromLevel();
        this.gameKit.gamePlayer.startLevel(
            uuid,
            () => {
                this.gameKit.gamePlayer.stopPlay();
                this.stopTest();
            },
            null,
            () => {
                this.gameKit.gamePlayer.stopPlay();
                this.stopTest();
            }
        );
        if (!this.gameKit.levelHasScreenshot(uuid)) {
            window.setTimeout(() => {
                this.gameKit.makeScreenshot(false);
            }, 100);
        }
    }

    // Stop testing the level.
    stopTest(dispatch = true) {
        this.gameKit.gamePlayer.stopPlay();
        this.gameKit.game._gsoSetPlayMode(false);
        if (this.gameKit.game._hasBody) {
            this.gameKit.game.destroyBody();
        }
        this.gameKit.gamePlayer.points = 0;
        this.gameKit.moveRenderer();
        this.gameKit.game.destroyVisuals();
        this.gameKit.game.createVisuals();
        this.gameKit.game.setViewPortScaling(ViewPortScaling.NativeResolution);
        this.attachToLevel(this.gameKit.game.activeLevel);

        if (dispatch) {
            store.dispatch(playerClose());
        }
    }

    // Start move tool mode
    startMoveMode() {
        this.inMoveMode = true;
        this.moveModeScale.x = 0;
        this.moveModeGoal = new Vector(1, 0.75);
        if (this.inTileMode) {
            this.tileEditor.detachFromStepParent();
            this.level.background.blur = true;
            this.level.background.requestNewVisuals();
        } else {
            this.blokEditor.detachFromLevel();
        }
    }

    // End move tool mode
    exitMoveMode() {
        this.inMoveMode = false;
        this.moveModeScale.x = 0;
        this.moveModeGoal = new Vector(1, 1);
        if (this.inTileMode) {
            this.tileEditor.attachToStepParent(this.level);
            this.level.background.blur = false;
            this.level.background.requestNewVisuals();
        } else {
            this.blokEditor.attachToLevel(this.level);
        }
    }

    // Start tile mode
    startTileMode() {
        this.inTileMode = true;
        this.level.background.blur = false;
        this.level.background.requestNewVisuals();
        this.blokEditor.detachFromLevel();
        this.tileEditor.attachToStepParent(this.level);
        this.tileEditor.startDrawMode();
    }

    // Exit tile mode
    exitTileMode() {
        this.inTileMode = false;
        this.level.background.blur = true;
        this.level.background.requestNewVisuals();
        this.tileEditor.detachFromStepParent();
        this.blokEditor.attachToLevel(this.level);
    }
}
